/**
 * Permission Constants
 *
 * These permission strings must match EXACTLY with the backend permission format.
 * Backend format: "Module.Resource.Action" (e.g., "Fleet.Vehicles.Read")
 *
 * Usage:
 * - In route guards: <RequirePermission permission={Permissions.Fleet.Vehicles.Read}>
 * - In hooks: const canCreate = usePermission(Permissions.Fleet.Vehicles.Create);
 * - In components: if (can(Permissions.Fleet.Vehicles.Update)) { ... }
 */


export const Permissions = {
  Fleet: {
    Vehicles: {
      Read: 'Fleet.Vehicles.Read',
      Create: 'Fleet.Vehicles.Create',
      Update: 'Fleet.Vehicles.Update',
      /** Manual vehicle odometer correction and voiding it; the backend also checks the unit scope. */
      CorrectOdometer: 'Fleet.Vehicles.CorrectOdometer',
      Assign: 'Fleet.Vehicles.Assign',
      Delete: 'Fleet.Vehicles.Delete',
      ReadAll: 'Fleet.Vehicles.ReadAll',
      ManageRequestPool: 'Fleet.Vehicles.ManageRequestPool',
    },
    Requests: {
      Read: 'Fleet.Requests.Read',
      Create: 'Fleet.Requests.Create',
      Approve: 'Fleet.Requests.Approve',
      Reject: 'Fleet.Requests.Reject',
      CreateForOthers: 'Fleet.Requests.CreateForOthers',
    },
    TravelOrders: {
      Read: 'Fleet.TravelOrders.Read',
      Create: 'Fleet.TravelOrders.Create',
      Manage: 'Fleet.TravelOrders.Manage',
      Close: 'Fleet.TravelOrders.Close',
      EditNote: 'Fleet.TravelOrders.Edit.Note',
    },
    TravelOrderPeriods: {
      Read: 'Fleet.TravelOrderPeriods.Read',
      Manage: 'Fleet.TravelOrderPeriods.Manage',
    },
    Providers: {
      Read: 'Fleet.Providers.Read',
      Manage: 'Fleet.Providers.Manage',
    },
    Contracts: {
      Read: 'Fleet.Contracts.Read',
      Manage: 'Fleet.Contracts.Manage',
    },
    Operations: {
      Read: 'Fleet.Operations.Read',
      Manage: 'Fleet.Operations.Manage',
      RegistrationManage: 'Fleet.Operations.Registration.Manage',
      MaintenanceManage: 'Fleet.Operations.Maintenance.Manage',
      TireManage: 'Fleet.Operations.Tire.Manage',
      FuelManage: 'Fleet.Operations.Fuel.Manage',
      FuelImport: 'Fleet.Operations.Fuel.Import',
    },
    FleetUsers: {
      Read: 'Fleet.FleetUsers.Read',
      Create: 'Fleet.FleetUsers.Create',
      Update: 'Fleet.FleetUsers.Update',
    },
    Tires: {
      Read: 'Fleet.Tires.Read',
      Create: 'Fleet.Tires.Create',
      Update: 'Fleet.Tires.Update',
      Delete: 'Fleet.Tires.Delete',
    },
    OperationalUnits: {
      Read: 'Fleet.OperationalUnits.Read',
      Manage: 'Fleet.OperationalUnits.Manage',
    },
    WorkOrders: {
      Read: 'Fleet.WorkOrders.Read',
      Manage: 'Fleet.WorkOrders.Manage',
      ManageService: 'Fleet.WorkOrders.Manage.Service',
      ManageTire: 'Fleet.WorkOrders.Manage.Tire',
    },
    Reports: {
      Run: 'Fleet.Reports.Run',
      ManageTemplates: 'Fleet.Reports.ManageTemplates',
    },
    Scope: {
      ManageAllUnits: 'Fleet.Scope.ManageAllUnits',
    },
  },
  Users: {
    Users: {
      Read: 'Users.Read',
      Create: 'Users.Create',
      Update: 'Users.Update',
      Archive: 'Users.Archive',
      Deactivate: 'Users.Deactivate',
    },
    Roles: {
      Read: 'Users.Roles.Read',
      Create: 'Users.Roles.Create',
      Update: 'Users.Roles.Update',
      Assign: 'Users.Roles.Assign',
    },
    Permissions: {
      Assign: 'Users.Permissions.Assign',
    },
  },
  HumanResources: {
    WorkUnits: {
      Read: 'HumanResources.WorkUnits.Read',
      Create: 'HumanResources.WorkUnits.Create',
      Update: 'HumanResources.WorkUnits.Update',
      Archive: 'HumanResources.WorkUnits.Archive',
      Restore: 'HumanResources.WorkUnits.Restore',
    },
    Employees: {
      Read: 'HumanResources.Employees.Read',
      Create: 'HumanResources.Employees.Create',
      Update: 'HumanResources.Employees.Update',
      Archive: 'HumanResources.Employees.Archive',
      Restore: 'HumanResources.Employees.Restore',
    },
    JobPositions: {
      Read: 'HumanResources.JobPositions.Read',
      Manage: 'HumanResources.JobPositions.Manage',
    },
    EmployeeAssignments: {
      Read: 'HumanResources.EmployeeAssignments.Read',
      Manage: 'HumanResources.EmployeeAssignments.Manage',
    },
    EmploymentPeriods: {
      Read: 'HumanResources.EmploymentPeriods.Read',
      Manage: 'HumanResources.EmploymentPeriods.Manage',
    },
    EmployeeEducations: {
      Read: 'HumanResources.EmployeeEducations.Read',
      Manage: 'HumanResources.EmployeeEducations.Manage',
    },
    EmployeePreviousSeniority: {
      Read: 'HumanResources.EmployeePreviousSeniority.Read',
      Manage: 'HumanResources.EmployeePreviousSeniority.Manage',
    },
    EmployeeLeaveEntitlements: {
      Read: 'HumanResources.EmployeeLeaveEntitlements.Read',
      Manage: 'HumanResources.EmployeeLeaveEntitlements.Manage',
    },
    EmployeeAbsences: {
      Read: 'HumanResources.EmployeeAbsences.Read',
      Manage: 'HumanResources.EmployeeAbsences.Manage',
    },
    EmployeeDocuments: {
      Read: 'HumanResources.EmployeeDocuments.Read',
      Manage: 'HumanResources.EmployeeDocuments.Manage',
    },
    Catalogs: {
      Manage: 'Hr.Catalogs.Manage',
    },
    Reports: {
      Run: 'HumanResources.Reports.Run',
      ManageTemplates: 'HumanResources.Reports.ManageTemplates',
      ShareTemplates: 'HumanResources.Reports.ShareTemplates',
      ViewAllData: 'HumanResources.Reports.ViewAllData',
    },
  },
  Documents: {
    Archive: {
      Open: 'Documents.Archive.Open',
    },
  },
  DocumentArchive: {
    Module: {
      Access: 'DocumentArchive.Module.Access',
    },
    Documents: {
      View: 'DocumentArchive.Documents.View',
      Upload: 'DocumentArchive.Documents.Upload',
      Download: 'DocumentArchive.Documents.Download',
      EditMetadata: 'DocumentArchive.Documents.EditMetadata',
      Delete: 'DocumentArchive.Documents.Delete',
      SendToArchive: 'DocumentArchive.Documents.SendToArchive',
      ManageAll: 'DocumentArchive.Documents.ManageAll',
    },
    Permissions: {
      Manage: 'DocumentArchive.Permissions.Manage',
    },
    Catalogs: {
      Manage: 'DocumentArchive.Catalogs.Manage',
    },
  },
} as const;

// Type helper for permission strings - provides autocomplete and type safety
export type PermissionString =
  | (typeof Permissions.Fleet.Vehicles)[keyof typeof Permissions.Fleet.Vehicles]
  | (typeof Permissions.Fleet.Requests)[keyof typeof Permissions.Fleet.Requests]
  | (typeof Permissions.Fleet.TravelOrders)[keyof typeof Permissions.Fleet.TravelOrders]
  | (typeof Permissions.Fleet.TravelOrderPeriods)[keyof typeof Permissions.Fleet.TravelOrderPeriods]
  | (typeof Permissions.Fleet.Providers)[keyof typeof Permissions.Fleet.Providers]
  | (typeof Permissions.Fleet.Contracts)[keyof typeof Permissions.Fleet.Contracts]
  | (typeof Permissions.Fleet.Operations)[keyof typeof Permissions.Fleet.Operations]
  | (typeof Permissions.Fleet.FleetUsers)[keyof typeof Permissions.Fleet.FleetUsers]
  | (typeof Permissions.Fleet.Tires)[keyof typeof Permissions.Fleet.Tires]
  | (typeof Permissions.Fleet.OperationalUnits)[keyof typeof Permissions.Fleet.OperationalUnits]
  | (typeof Permissions.Fleet.WorkOrders)[keyof typeof Permissions.Fleet.WorkOrders]
  | (typeof Permissions.Fleet.Scope)[keyof typeof Permissions.Fleet.Scope]
  | (typeof Permissions.Users.Users)[keyof typeof Permissions.Users.Users]
  | (typeof Permissions.Users.Roles)[keyof typeof Permissions.Users.Roles]
  | (typeof Permissions.Users.Permissions)[keyof typeof Permissions.Users.Permissions]
  | (typeof Permissions.HumanResources.WorkUnits)[keyof typeof Permissions.HumanResources.WorkUnits]
  | (typeof Permissions.HumanResources.Employees)[keyof typeof Permissions.HumanResources.Employees]
  | (typeof Permissions.HumanResources.JobPositions)[keyof typeof Permissions.HumanResources.JobPositions]
  | (typeof Permissions.HumanResources.EmployeeAssignments)[keyof typeof Permissions.HumanResources.EmployeeAssignments]
  | (typeof Permissions.HumanResources.EmploymentPeriods)[keyof typeof Permissions.HumanResources.EmploymentPeriods]
  | (typeof Permissions.HumanResources.EmployeeEducations)[keyof typeof Permissions.HumanResources.EmployeeEducations]
  | (typeof Permissions.HumanResources.EmployeePreviousSeniority)[keyof typeof Permissions.HumanResources.EmployeePreviousSeniority]
  | (typeof Permissions.HumanResources.EmployeeLeaveEntitlements)[keyof typeof Permissions.HumanResources.EmployeeLeaveEntitlements]
  | (typeof Permissions.HumanResources.EmployeeAbsences)[keyof typeof Permissions.HumanResources.EmployeeAbsences]
  | (typeof Permissions.HumanResources.EmployeeDocuments)[keyof typeof Permissions.HumanResources.EmployeeDocuments]
  | (typeof Permissions.HumanResources.Catalogs)[keyof typeof Permissions.HumanResources.Catalogs]
  | (typeof Permissions.HumanResources.Reports)[keyof typeof Permissions.HumanResources.Reports]
  | (typeof Permissions.Documents.Archive)[keyof typeof Permissions.Documents.Archive]
  | (typeof Permissions.DocumentArchive.Module)[keyof typeof Permissions.DocumentArchive.Module]
  | (typeof Permissions.DocumentArchive.Documents)[keyof typeof Permissions.DocumentArchive.Documents]
  | (typeof Permissions.DocumentArchive.Permissions)[keyof typeof Permissions.DocumentArchive.Permissions]
  | (typeof Permissions.DocumentArchive.Catalogs)[keyof typeof Permissions.DocumentArchive.Catalogs];

/**
 * Helper function to check if a string is a valid permission
 */
export const isValidPermission = (permission: string): permission is PermissionString => {
  const allPermissions = Object.values(Permissions).flatMap((module) =>
    Object.values(module).flatMap((resource) => Object.values(resource))
  );
  return allPermissions.includes(permission);
};
